import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import { ArrowLeft, Save, Upload, Trash2, Plus, GripVertical, X, Wand2, FileStack, ChevronUp, ChevronDown, Image as ImageIcon, Copy, Archive } from "lucide-react";

const categories = ["Safe Work Instruction","Standard Operating Procedure","Maintenance Procedure","Inspection Form","Policy","Technical Report","Checklist","Risk Assessment","Custom"];

export default function TemplateDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { activeWorkspace } = useAuth();
  const [tpl, setTpl] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [logoBlob, setLogoBlob] = useState(null);
  const [analysing, setAnalysing] = useState(false);
  const fileInput = useRef(null);
  const analyseInput = useRef(null);

  const load = async () => {
    const { data } = await api.get(`/workspaces/${activeWorkspace.id}/templates/${id}`);
    setTpl(data);
    if (data.logo_path) fetchLogo();
    else setLogoBlob(null);
  };
  useEffect(() => { if (activeWorkspace) load(); /* eslint-disable-next-line */ }, [activeWorkspace, id]);

  const fetchLogo = async () => {
    try {
      const token = localStorage.getItem("fx_token");
      const r = await fetch(`${BACKEND_URL}/api/workspaces/${activeWorkspace.id}/templates/${id}/logo`, { headers: { Authorization: `Bearer ${token}` }});
      if (r.ok) { const b = await r.blob(); setLogoBlob(URL.createObjectURL(b)); }
    } catch {}
  };

  const change = (patch) => { setTpl({ ...tpl, ...patch }); setDirty(true); };
  const changeSection = (idx, patch) => {
    const sections = tpl.sections.map((s, i) => i === idx ? { ...s, ...patch } : s);
    change({ sections });
  };
  const addSection = () => change({ sections: [...(tpl.sections||[]), { name: "New section", required: false, guidance: "" }] });
  const removeSection = (idx) => change({ sections: tpl.sections.filter((_, i) => i !== idx) });
  const moveSection = (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= tpl.sections.length) return;
    const arr = [...tpl.sections];
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    change({ sections: arr });
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/workspaces/${activeWorkspace.id}/templates/${id}`, {
        name: tpl.name, category: tpl.category, description: tpl.description,
        document_prefix: tpl.document_prefix, sections: tpl.sections, status: tpl.status,
        metadata_fields: tpl.metadata_fields || [],
        style: tpl.style || {},
        cover_page: tpl.cover_page || "none",
        contents_page: tpl.contents_page || "none",
        contents_title: tpl.contents_title || "Contents",
        contents_depth: tpl.contents_depth || 3,
      });
      setTpl(data); setDirty(false); toast.success("Template saved");
    } catch { toast.error("Could not save"); }
    finally { setBusy(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Logo must be an image (PNG or JPG)"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/workspaces/${activeWorkspace.id}/templates/${id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo uploaded");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not upload logo"); }
    finally { setBusy(false); }
  };
  const removeLogo = async () => {
    if (!window.confirm("Remove logo from this template?")) return;
    setBusy(true);
    try { await api.delete(`/workspaces/${activeWorkspace.id}/templates/${id}/logo`); setLogoBlob(null); toast.success("Logo removed"); await load(); }
    catch { toast.error("Could not remove logo"); }
    finally { setBusy(false); }
  };

  const analyseDocx = async (file) => {
    if (!file) return;
    setAnalysing(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/workspaces/${activeWorkspace.id}/templates/analyse`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (!data.sections?.length && !data.style) { toast.error("No structure or style detected in that file"); return; }
      const existingNames = new Set((tpl.sections || []).map(s => s.name.toLowerCase()));
      const added = (data.sections || []).filter(s => !existingNames.has(s.name.toLowerCase()));
      const patch = { sections: [...(tpl.sections || []), ...added] };
      if (data.style && Object.keys(data.style).length) {
        patch.style = { ...(tpl.style || {}), ...data.style };
      }
      change(patch);
      const parts = [];
      if (added.length) parts.push(`${added.length} sections`);
      if (data.style && Object.keys(data.style).length) parts.push(`fonts & colours`);
      toast.success(`Imported ${parts.join(" + ") || "structure"} from ${file.name}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not analyse Word file"); }
    finally { setAnalysing(false); }
  };

  const duplicate = async () => {
    try { const { data } = await api.post(`/workspaces/${activeWorkspace.id}/templates/${id}/duplicate`); nav(`/app/templates/${data.id}`); toast.success("Duplicated"); }
    catch { toast.error("Could not duplicate"); }
  };
  const archive = async () => {
    if (!window.confirm("Archive this template? It won't appear in new-document flows.")) return;
    try { await api.delete(`/workspaces/${activeWorkspace.id}/templates/${id}`); toast.success("Archived"); nav("/app/templates"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not archive"); }
  };

  if (!tpl) return <div className="p-10 text-slate-400" data-testid="tpl-loading">Loading template…</div>;

  return (
    <div className="max-w-6xl mx-auto p-8 space-y-6" data-testid="template-detail">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/app/templates" className="text-slate-400 hover:text-white p-1" data-testid="tpl-back"><ArrowLeft size={16}/></Link>
          <div className="min-w-0">
            <div className="mono text-[10px] uppercase tracking-widest text-slate-500">{tpl.category}</div>
            <h1 className="font-display text-2xl font-bold text-white truncate">{tpl.name}</h1>
            <div className="mt-1 flex items-center gap-2"><StatusBadge status={tpl.status}/><span className="text-xs text-slate-500">v{tpl.current_version} · {tpl.sections?.length || 0} sections · {tpl.usage_count || 0} uses</span></div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={duplicate} className="border border-white/10 hover:border-white/20 text-slate-200 text-sm px-3 py-1.5 rounded-lg inline-flex items-center gap-1" data-testid="tpl-duplicate"><Copy size={14}/> Duplicate</button>
          <button onClick={archive} className="border border-red-500/30 hover:bg-red-500/10 text-red-300 text-sm px-3 py-1.5 rounded-lg inline-flex items-center gap-1" data-testid="tpl-archive"><Archive size={14}/> Archive</button>
          <button onClick={save} disabled={!dirty || busy} className="fx-btn-primary text-white text-sm font-medium px-4 py-2 rounded-lg inline-flex items-center gap-2 disabled:opacity-50" data-testid="tpl-save"><Save size={14}/>{busy?"Saving…": dirty ? "Save changes" : "Saved"}</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Details */}
        <div className="fx-panel p-5 lg:col-span-2 space-y-4">
          <div className="mono text-[10px] uppercase tracking-widest text-slate-500">Details</div>
          <Field label="Template name" value={tpl.name} onChange={(v)=>change({name:v})} testid="tpl-edit-name"/>
          <div className="grid md:grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs text-slate-400 mb-1">Category</div>
              <select value={tpl.category} onChange={(e)=>change({category:e.target.value})} className="w-full bg-[#0D1224] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-slate-100" data-testid="tpl-edit-category">
                {categories.concat(categories.includes(tpl.category)?[]:[tpl.category]).map(c=> <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <Field label="Document number prefix" value={tpl.document_prefix || ""} onChange={(v)=>change({document_prefix:v})} placeholder="e.g. SWI" testid="tpl-edit-prefix"/>
          </div>
          <label className="block">
            <div className="text-xs text-slate-400 mb-1">Description</div>
            <textarea rows={3} value={tpl.description || ""} onChange={(e)=>change({description:e.target.value})} className="w-full bg-[#0D1224] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-slate-100 focus:border-cyan-400 outline-none" data-testid="tpl-edit-desc"/>
          </label>
          <label className="block">
            <div className="text-xs text-slate-400 mb-1">Status</div>
            <select value={tpl.status} onChange={(e)=>change({status:e.target.value})} className="w-full max-w-xs bg-[#0D1224] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-slate-100" data-testid="tpl-edit-status">
              <option value="draft">Draft</option><option value="active">Active</option><option value="archived">Archived</option>
            </select>
          </label>
        </div>

        {/* Logo */}
        <div className="fx-panel p-5 space-y-3">
          <div className="mono text-[10px] uppercase tracking-widest text-slate-500">Template logo</div>
          <div className="border border-[#1E293B] rounded-lg p-4 bg-[#0A0F1F] flex items-center justify-center h-40">
            {logoBlob ? <img src={logoBlob} alt="logo" className="max-h-32 max-w-full object-contain"/> : <div className="text-center text-slate-500 text-sm"><ImageIcon size={22} className="mx-auto mb-2 text-slate-600"/>No logo yet</div>}
          </div>
          <div className="text-xs text-slate-500">Appears at the top of every generated document that uses this template. PNG, JPG or WebP, up to 3 MB.</div>
          <div className="flex gap-2">
            <button onClick={()=>fileInput.current?.click()} disabled={busy} className="flex-1 border border-white/10 hover:border-cyan-400/40 text-slate-200 text-xs px-3 py-2 rounded-lg inline-flex items-center justify-center gap-1" data-testid="tpl-logo-upload"><Upload size={12}/> {logoBlob?"Replace":"Upload logo"}</button>
            {logoBlob && <button onClick={removeLogo} disabled={busy} className="border border-red-500/30 hover:bg-red-500/10 text-red-300 text-xs px-3 py-2 rounded-lg inline-flex items-center gap-1" data-testid="tpl-logo-remove"><Trash2 size={12}/> Remove</button>}
            <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e)=>e.target.files[0] && uploadLogo(e.target.files[0])} data-testid="tpl-logo-input"/>
          </div>
        </div>
      </div>

      {/* Page structure */}
      <div className="fx-panel p-5" data-testid="tpl-page-structure">
        <div className="mono text-[10px] uppercase tracking-widest text-slate-500">Page structure</div>
        <div className="text-sm text-slate-400 mb-4">Defaults for every document created from this template. Authors can override them per document.</div>
        <div className="grid md:grid-cols-4 gap-4">
          <label className="block">
            <div className="text-xs text-slate-400 mb-1">Cover page</div>
            <select value={tpl.cover_page || "none"} onChange={(e)=>change({cover_page:e.target.value})} className="w-full bg-[#0D1224] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-slate-100" data-testid="tpl-cover-page">
              <option value="none">No cover page</option>
              <option value="standard">Standard cover page</option>
            </select>
          </label>
          <label className="block">
            <div className="text-xs text-slate-400 mb-1">Contents page</div>
            <select value={tpl.contents_page || "none"} onChange={(e)=>change({contents_page:e.target.value})} className="w-full bg-[#0D1224] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-slate-100" data-testid="tpl-contents-page">
              <option value="none">No contents page</option>
              <option value="auto">Automatic contents page</option>
            </select>
          </label>
          <label className="block">
            <div className="text-xs text-slate-400 mb-1">Contents title</div>
            <input value={tpl.contents_title || "Contents"} onChange={(e)=>change({contents_title:e.target.value})} className="w-full bg-[#0D1224] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-slate-100 focus:border-cyan-400 outline-none" data-testid="tpl-contents-title"/>
          </label>
          <label className="block">
            <div className="text-xs text-slate-400 mb-1">Heading depth</div>
            <select value={tpl.contents_depth || 3} onChange={(e)=>change({contents_depth:Number(e.target.value)})} className="w-full bg-[#0D1224] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-slate-100" data-testid="tpl-contents-depth">
              <option value={1}>Level 1 only</option>
              <option value={2}>Levels 1–2</option>
              <option value={3}>Levels 1–3</option>
            </select>
          </label>
        </div>
        <div className="text-[11px] text-slate-500 mt-3">The contents page is exported as a real Microsoft Word table-of-contents field, so page numbers stay accurate. Word may ask to update the field the first time the file is opened.</div>
      </div>

      {/* Sections */}
      <div className="fx-panel p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-slate-500">Sections</div>
            <div className="text-sm text-slate-400">Order matters — Fexidoc generates sections in the order listed here.</div>
          </div>
          <div className="flex gap-2">
            <button onClick={()=>analyseInput.current?.click()} disabled={analysing} className="border border-cyan-500/40 hover:bg-cyan-500/10 text-cyan-300 text-xs px-3 py-1.5 rounded-lg inline-flex items-center gap-1" data-testid="tpl-analyse"><Wand2 size={12}/>{analysing?"Analysing…":"Extract from Word"}</button>
            <button onClick={addSection} className="border border-white/10 hover:border-white/20 text-slate-200 text-xs px-3 py-1.5 rounded-lg inline-flex items-center gap-1" data-testid="tpl-add-section"><Plus size={12}/>Add section</button>
            <input ref={analyseInput} type="file" accept=".docx" className="hidden" onChange={(e)=>e.target.files[0] && analyseDocx(e.target.files[0])} data-testid="tpl-analyse-input"/>
          </div>
        </div>
        <div className="space-y-2">
          {(tpl.sections || []).map((s, idx) => (
            <div key={idx} className="border border-[#1E293B] rounded-lg p-3 flex items-start gap-3" data-testid={`section-row-${idx}`}>
              <div className="flex flex-col shrink-0 pt-1">
                <button onClick={()=>moveSection(idx, -1)} disabled={idx===0} className="text-slate-500 hover:text-white disabled:opacity-30" data-testid={`section-up-${idx}`}><ChevronUp size={14}/></button>
                <div className="mono text-[10px] text-slate-600 text-center">{String(idx+1).padStart(2,"0")}</div>
                <button onClick={()=>moveSection(idx, 1)} disabled={idx===tpl.sections.length-1} className="text-slate-500 hover:text-white disabled:opacity-30" data-testid={`section-down-${idx}`}><ChevronDown size={14}/></button>
              </div>
              <div className="flex-1 min-w-0 space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <input value={s.name} onChange={(e)=>changeSection(idx, {name:e.target.value})} placeholder="Section name" className="flex-1 min-w-[180px] bg-[#050816] border border-[#1E293B] rounded-lg px-3 py-1.5 text-sm text-slate-100 focus:border-cyan-400 outline-none" data-testid={`section-name-${idx}`}/>
                  <label className="text-xs text-slate-300 inline-flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={!!s.required} onChange={(e)=>changeSection(idx, {required:e.target.checked})} className="accent-cyan-400" data-testid={`section-required-${idx}`}/>
                    Required
                  </label>
                  <button onClick={()=>removeSection(idx)} className="text-slate-500 hover:text-red-400 p-1" data-testid={`section-remove-${idx}`}><Trash2 size={14}/></button>
                </div>
                <textarea value={s.guidance || ""} onChange={(e)=>changeSection(idx, {guidance:e.target.value})} rows={2} placeholder="Optional generation guidance for this section — e.g. 'List every PPE item required by the site standard.'" className="w-full bg-[#050816] border border-[#1E293B] rounded-lg px-3 py-2 text-xs text-slate-200 focus:border-cyan-400 outline-none" data-testid={`section-guidance-${idx}`}/>
              </div>
            </div>
          ))}
          {(tpl.sections || []).length === 0 && (
            <div className="text-center text-sm text-slate-500 py-8 border-2 border-dashed border-[#1E293B] rounded-lg">
              No sections yet. <button onClick={addSection} className="text-cyan-300 hover:text-cyan-200">Add one</button> or <button onClick={()=>analyseInput.current?.click()} className="text-cyan-300 hover:text-cyan-200">extract from a Word file</button>.
            </div>
          )}
        </div>
      </div>

      {/* Custom metadata fields */}
      <MetadataFieldsPanel tpl={tpl} onChange={change}/>

      {/* Style hints from Word */}
      <StylePanel tpl={tpl} onChange={change}/>

      {/* Sticky save bar when dirty */}
      {dirty && (
        <div className="sticky bottom-4 fx-panel p-3 flex items-center justify-between shadow-xl" data-testid="dirty-bar">
          <div className="text-sm text-amber-300 inline-flex items-center gap-2"><span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse"/> Unsaved changes</div>
          <button onClick={save} disabled={busy} className="fx-btn-primary text-white text-sm font-medium px-4 py-2 rounded-lg inline-flex items-center gap-2 disabled:opacity-50" data-testid="dirty-save"><Save size={14}/> {busy?"Saving…":"Save changes"}</button>
        </div>
      )}
    </div>
  );
}

function Field({label, value, onChange, placeholder, testid}) {
  return (<label className="block"><div className="text-xs text-slate-400 mb-1">{label}</div><input value={value} onChange={(e)=>onChange(e.target.value)} placeholder={placeholder} className="w-full bg-[#0D1224] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-slate-100 focus:border-cyan-400 outline-none" data-testid={testid}/></label>);
}

const FIELD_TYPES = [
  { k:"text", label:"Text" },
  { k:"long_text", label:"Long text" },
  { k:"number", label:"Number" },
  { k:"date", label:"Date" },
  { k:"dropdown", label:"Dropdown" },
  { k:"person", label:"Person" },
  { k:"toggle", label:"Toggle" },
];

function slugKey(label) {
  return (label || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 40) || "field";
}

function MetadataFieldsPanel({ tpl, onChange }) {
  const fields = tpl.metadata_fields || [];
  const setFields = (fields) => onChange({ metadata_fields: fields });
  const add = () => setFields([...fields, { key: `field_${fields.length+1}`, label: `Field ${fields.length+1}`, type: "text", required: false, options: [], help: "" }]);
  const update = (i, patch) => setFields(fields.map((f, idx) => idx===i ? {...f, ...patch} : f));
  const remove = (i) => setFields(fields.filter((_, idx) => idx !== i));
  const move = (i, d) => {
    const t = i + d;
    if (t<0 || t>=fields.length) return;
    const arr = [...fields]; [arr[i], arr[t]] = [arr[t], arr[i]]; setFields(arr);
  };
  return (
    <div className="fx-panel p-5" data-testid="metadata-fields-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-slate-500">Custom metadata fields</div>
          <div className="text-sm text-slate-400">These appear in the Create Document details step for this template.</div>
        </div>
        <button onClick={add} className="border border-white/10 hover:border-white/20 text-slate-200 text-xs px-3 py-1.5 rounded-lg inline-flex items-center gap-1" data-testid="add-metadata-field"><Plus size={12}/>Add field</button>
      </div>
      <div className="space-y-2">
        {fields.map((f, idx) => (
          <div key={idx} className="border border-[#1E293B] rounded-lg p-3 space-y-2" data-testid={`meta-field-${idx}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex flex-col shrink-0">
                <button onClick={()=>move(idx,-1)} disabled={idx===0} className="text-slate-500 hover:text-white disabled:opacity-30"><ChevronUp size={12}/></button>
                <button onClick={()=>move(idx,1)} disabled={idx===fields.length-1} className="text-slate-500 hover:text-white disabled:opacity-30"><ChevronDown size={12}/></button>
              </div>
              <input value={f.label} onChange={(e)=>{const label=e.target.value; update(idx, {label, key: f.key && f.key !== slugKey(f.label) ? f.key : slugKey(label)});}} placeholder="Label (e.g. Contract Number)" className="flex-1 min-w-[160px] bg-[#050816] border border-[#1E293B] rounded-lg px-3 py-1.5 text-sm text-slate-100 focus:border-cyan-400 outline-none" data-testid={`meta-label-${idx}`}/>
              <select value={f.type} onChange={(e)=>update(idx, {type: e.target.value})} className="bg-[#050816] border border-[#1E293B] rounded-lg px-2 py-1.5 text-sm text-slate-100" data-testid={`meta-type-${idx}`}>
                {FIELD_TYPES.map(t => <option key={t.k} value={t.k}>{t.label}</option>)}
              </select>
              <label className="text-xs text-slate-300 inline-flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={!!f.required} onChange={(e)=>update(idx, {required: e.target.checked})} className="accent-cyan-400" data-testid={`meta-required-${idx}`}/>
                Required
              </label>
              <button onClick={()=>remove(idx)} className="text-slate-500 hover:text-red-400 p-1" data-testid={`meta-remove-${idx}`}><Trash2 size={14}/></button>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <input value={f.key || ""} onChange={(e)=>update(idx, {key: slugKey(e.target.value)})} placeholder="internal key" className="w-40 bg-[#050816] border border-[#1E293B] rounded-lg px-2 py-1 text-xs mono text-slate-300 outline-none" data-testid={`meta-key-${idx}`}/>
              <input value={f.help || ""} onChange={(e)=>update(idx, {help: e.target.value})} placeholder="Help text (optional)" className="flex-1 min-w-[160px] bg-[#050816] border border-[#1E293B] rounded-lg px-2 py-1 text-xs text-slate-300 outline-none"/>
            </div>
            {f.type === "dropdown" && (
              <div>
                <div className="text-[11px] text-slate-500 mb-1">Options (comma separated)</div>
                <input value={(f.options || []).join(", ")} onChange={(e)=>update(idx, {options: e.target.value.split(",").map(s => s.trim()).filter(Boolean)})} placeholder="Draft, Under Review, Approved" className="w-full bg-[#050816] border border-[#1E293B] rounded-lg px-2 py-1.5 text-xs text-slate-200 outline-none" data-testid={`meta-options-${idx}`}/>
              </div>
            )}
          </div>
        ))}
        {fields.length === 0 && (
          <div className="text-center text-sm text-slate-500 py-6 border-2 border-dashed border-[#1E293B] rounded-lg">
            No custom fields yet. Fexidoc already collects Title, Number, Revision, Department, Site, Author, Reviewer, Approver and Effective date.
            <button onClick={add} className="ml-2 text-cyan-300 hover:text-cyan-200">Add one</button>
          </div>
        )}
      </div>
    </div>
  );
}

function StylePanel({ tpl, onChange }) {
  const style = tpl.style || {};
  const setStyle = (patch) => onChange({ style: { ...style, ...patch } });
  return (
    <div className="fx-panel p-5" data-testid="style-panel">
      <div className="mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">Document style</div>
      <div className="text-sm text-slate-400 mb-4">Applied to every Word document generated from this template. Extract from a reference .docx to fill these automatically.</div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
        <StyleField label="Body font" value={style.body_font || ""} onChange={(v)=>setStyle({body_font:v})} placeholder="Calibri" testid="style-body-font"/>
        <StyleField label="Body size (pt)" value={style.body_size || ""} onChange={(v)=>setStyle({body_size: v ? Number(v) : ""})} placeholder="11" type="number" testid="style-body-size"/>
        <StyleField label="Heading font" value={style.heading_font || ""} onChange={(v)=>setStyle({heading_font:v})} placeholder="Manrope" testid="style-heading-font"/>
        <StyleField label="Heading size (pt)" value={style.heading_size || ""} onChange={(v)=>setStyle({heading_size: v ? Number(v) : ""})} placeholder="16" type="number" testid="style-heading-size"/>
        <label className="block">
          <div className="text-xs text-slate-400 mb-1">Heading colour</div>
          <div className="flex items-center gap-2">
            <input type="color" value={style.heading_color || "#1478FF"} onChange={(e)=>setStyle({heading_color: e.target.value})} className="w-10 h-10 rounded-md bg-transparent border border-[#1E293B]" data-testid="style-heading-color"/>
            <input value={style.heading_color || ""} onChange={(e)=>setStyle({heading_color: e.target.value})} placeholder="#1478FF" className="flex-1 bg-[#0D1224] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-slate-100 mono focus:border-cyan-400 outline-none"/>
          </div>
        </label>
      </div>
      {Object.keys(style).length > 0 && (
        <div className="mt-4 flex items-center gap-3 bg-white text-slate-900 p-4 rounded-md border border-slate-200" data-testid="style-preview">
          <div style={{fontFamily: style.heading_font || 'Manrope', color: style.heading_color || '#0b3b7a', fontSize: `${style.heading_size || 16}pt`, fontWeight: 700}}>Heading Preview</div>
          <div style={{fontFamily: style.body_font || 'Calibri', fontSize: `${style.body_size || 11}pt`, color: '#334155'}}>Body sample text.</div>
        </div>
      )}
    </div>
  );
}

function StyleField({label, value, onChange, placeholder, type="text", testid}) {
  return (<label className="block"><div className="text-xs text-slate-400 mb-1">{label}</div><input type={type} value={value} onChange={(e)=>onChange(e.target.value)} placeholder={placeholder} className="w-full bg-[#0D1224] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-slate-100 focus:border-cyan-400 outline-none" data-testid={testid}/></label>);
}
